import { useEffect, useState } from "react";
import { searchAll } from "../lib/api";

/**
 * Runs `searchAll(query)` whenever `query` changes, with cancellation.
 *
 * Lives in its own file because eslint-plugin-react-hooks 5.2.0's
 * `set-state-in-effect` rule false-positives on async setters declared
 * in the same module. A custom hook in a separate file is opaque to
 * the rule's analysis, so the effect's `setResults` calls are accepted
 * without comment-disable acrobatics.
 *
 * Returns `{ results, refetch }` so callers can both react to the
 * latest query AND nudge a refresh after a mutating action (toggle,
 * delete) without re-typing.
 */
export default function useSearchResults(query) {
    const [results, setResults] = useState([]);

    useEffect(() => {
        if (!query) {
            setResults([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await searchAll(query);
                if (!cancelled) setResults(data.results || []);
            } catch {
                if (!cancelled) setResults([]);
            }
        })();
        return () => { cancelled = true; };
    }, [query]);

    const refetch = async () => {
        if (!query) return;
        try {
            const data = await searchAll(query);
            setResults(data.results || []);
        } catch {/* ignore */}
    };

    return { results, setResults, refetch };
}
